import {
  BookOpenText,
  Bot,
  Building2,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  MessageSquareText,
  PhoneCall,
  Send,
  ShieldCheck,
  Sparkles,
  Tags,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { getLang, t } from "../i18n";

const primaryItems = [
  { icon: LayoutDashboard, label: "dashboard", to: "/admin", end: true },
  { icon: PhoneCall, label: "session", to: "/admin/session" },
  { icon: MessageSquareText, label: "chat", to: "/admin/chat" },
  { icon: MessageSquareText, label: "team-inbox", to: "/admin/inbox" },
  { icon: Send, label: "send-message", to: "/admin/send-message" },
  { icon: Bot, label: "auto-reply", to: "/admin/auto-reply" },
  { icon: ShieldCheck, label: "growth", to: "/admin/growth" },
  { icon: Sparkles, label: "automation-studio", to: "/admin/automation-studio" },
  { icon: Users, label: "client", to: "/admin/client" },
  { icon: Tags, label: "groups", to: "/admin/groups" },
];

export default function Sidebar({ open = false, onToggle, onNavigate }) {
  const isRTL = getLang() === "ar";
  const username = localStorage.getItem("user") || (isRTL ? "مستخدم" : "User");
  const role = localStorage.getItem("role") || "client";
  const initials = username.trim().slice(0, 2).toUpperCase();
  const hiddenTransform = isRTL ? "translate-x-full" : "-translate-x-full";
  const sidePosition = isRTL ? "right-0" : "left-0";
  const sideBorder = isRTL ? "border-l" : "border-r";
  const CollapseIcon = isRTL ? ChevronRight : ChevronLeft;

  const renderItem = ({ icon: Icon, label, to, end = false }) => (
    <li key={to}>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        title={!open ? t(label) : undefined}
        className={({ isActive }) =>
          `sidebar-link ${isActive ? "sidebar-link-active" : ""} ${!open ? "lg:justify-center" : ""}`
        }
      >
        <Icon size={20} strokeWidth={1.8} aria-hidden="true" />
        <span className={`${open ? "lg:inline" : "lg:hidden"}`}>{t(label)}</span>
      </NavLink>
    </li>
  );

  return (
    <aside
      className={`fixed ${sidePosition} top-0 z-40 h-dvh border-slate-200 bg-white transition-[width,transform] duration-300 ${sideBorder} ${
        open ? "w-72 translate-x-0" : `w-72 ${hiddenTransform} lg:w-20 lg:translate-x-0`
      }`}
      dir={isRTL ? "rtl" : "ltr"}
      aria-label={isRTL ? "التنقل الرئيسي" : "Main navigation"}
    >
      <div className="flex h-full flex-col">
        <div className={`flex h-[72px] items-center border-b border-slate-100 px-4 ${open ? "justify-between" : "lg:justify-center"}`}>
          <NavLink to="/admin" onClick={onNavigate} className={`brand-lockup ${open ? "flex" : "flex lg:hidden"}`}>
            <span className="brand-symbol" aria-hidden="true">و</span>
            <span>واتسي</span>
          </NavLink>
          <button
            type="button"
            onClick={onToggle}
            className="icon-button hidden lg:inline-flex"
            aria-label={open ? (isRTL ? "طي القائمة" : "Collapse navigation") : (isRTL ? "توسيع القائمة" : "Expand navigation")}
          >
            <CollapseIcon className={!open ? "rotate-180" : ""} size={19} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <p className={`sidebar-section-label ${!open ? "lg:sr-only" : ""}`}>
            {isRTL ? "مساحة العمل" : "Workspace"}
          </p>
          <ul className="space-y-1">{primaryItems.map(renderItem)}</ul>

          <div className="my-4 border-t border-slate-100" />
          <ul className="space-y-1">
            {renderItem({ icon: Building2, label: "workspace-settings", to: "/admin/workspace" })}
            {renderItem({ icon: BookOpenText, label: "api-docs", to: "/admin/api-docs" })}
            {role === "superadmin" && renderItem({ icon: Users, label: "users", to: "/admin/users" })}
          </ul>
        </nav>

        <NavLink
          to="/admin/profile"
          onClick={onNavigate}
          className={`m-3 flex items-center gap-3 rounded-xl border border-slate-200 p-2.5 transition-colors hover:bg-slate-50 ${!open ? "lg:justify-center" : ""}`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-950 text-sm font-semibold text-white">
            {initials}
          </span>
          <span className={`min-w-0 ${open ? "lg:block" : "lg:hidden"}`}>
            <strong className="block truncate text-sm text-slate-900">{username}</strong>
            <span className="block truncate text-xs capitalize text-slate-500">{role}</span>
          </span>
        </NavLink>
      </div>
    </aside>
  );
}
